import { useCallback } from 'react';
import { getHdeEventCalendarIcsUrl } from '~/modules/hde/api/get-hde-event-calendar-ics-url';
import { getHdeEventGoogleCalendarUrl } from '~/modules/hde/api/get-hde-event-google-calendar-url';
import { getHdeEventOutlookCalendarUrl } from '~/modules/hde/api/get-hde-event-outlook-calendar-url';
import { HDE_STEP_CONFIGS } from './steps';
import {
    useHDERegistrationForm,
    type FilterVisibleStepsContext,
    type GetInitialDataContext,
} from '../../hooks/useHDERegistrationForm';
import type { User } from '~/server/types';
import type { GroupedSelectOptionType } from '../../utils/form-options';
import type { StageConfiguration } from '@modules/build-hub/configs/stage-config';
import {
    getHdeEventCountry,
    getPrimaryEventId,
    type LocationOption,
} from './types';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';
import {
    resolveHdeEventId,
    mapExistingSubmissionToHomeownerFormData,
} from '../../utils/map-existing-submission-to-form-data';

interface HDERegistrationFormProps {
    stageConfig: StageConfiguration;
    locationOptions: LocationOption[];
    layout?: 'single' | 'split';
    onClose?: () => void;
    onStepChange?: (stepId: string) => void;
    onAuthViewChange?: (showEmailForm: boolean) => void;
    onAuthLoadingChange?: (loading: boolean) => void;
    socialLoginSubmitted?: boolean;
    socialLoginEventId?: string | null;
    onBackToApplicationType?: () => void;
    onSwitchApplicationType?: (
        type: 'Homeowner' | 'Professional' | 'Exhibitor',
    ) => void;
    suppressExistingSubmissionGate?: boolean;
    onExistingSubmissionGateResolved?: () => void;
}

interface FormData {
    eventId: string | null;
    attendanceReasons: GroupedSelectOptionType[];
    otherReason: string;
    productInterests: GroupedSelectOptionType[];
    otherProductInterest: string;
    projectStage: string | null;
    projectType: 'Residential' | 'Commercial' | null;
    projectScope: string | null;
    projectLocation: string;
    selectedLocation: DetailedPlace | null;
    ownerType: 'owner' | 'professional' | null;
    budget: string | null;
    bringGuest: boolean | null;
    guestFormView: boolean;
    guestName: string;
    guestEmail: string;
    sameContactDetails: boolean;
}

const INITIAL_FORM_DATA: FormData = {
    eventId: null,
    attendanceReasons: [],
    otherReason: '',
    productInterests: [],
    otherProductInterest: '',
    projectStage: null,
    projectType: 'Residential',
    projectScope: null,
    projectLocation: '',
    selectedLocation: null,
    ownerType: null,
    budget: null,
    bringGuest: null,
    guestFormView: false,
    guestName: '',
    guestEmail: '',
    sameContactDetails: false,
};

export function HDERegistrationForm({
    stageConfig,
    locationOptions,
    layout = 'single',
    onClose,
    onStepChange,
    onAuthViewChange,
    onAuthLoadingChange,
    socialLoginSubmitted = false,
    socialLoginEventId = null,
    onBackToApplicationType,
    onSwitchApplicationType,
    suppressExistingSubmissionGate,
    onExistingSubmissionGateResolved,
}: HDERegistrationFormProps) {
    const filterVisibleSteps = useCallback(
        (
            configs: typeof HDE_STEP_CONFIGS,
            context: FilterVisibleStepsContext,
        ) => {
            const formData = context.formData as FormData | undefined;
            const hasProductSuppliersReason =
                formData?.attendanceReasons?.some(
                    (r) => r.value === 'Product Suppliers',
                ) ?? false;

            return configs.filter((step) => {
                if (step.id === 'existing-submission')
                    return context.hasExistingSubmission ?? false;
                if (step.id === 'authentication') return !context.isLoggedIn;
                if (step.id === 'product-interests')
                    return hasProductSuppliersReason;
                return true;
            });
        },
        [],
    );

    const getInitialDataForStep = useCallback(
        (stepId: string, formData: FormData, ctx: GetInitialDataContext) => {
            const stageCfg = ctx.stageConfig as StageConfiguration | undefined;
            const opts = Array.isArray(ctx.locationOptions)
                ? (ctx.locationOptions as LocationOption[])
                : [];
            /** Event ID for calendar: coming event closest to now (first in opts, sorted by nearest upcoming date). */
            const eventIdForCalendar =
                opts.length > 0
                    ? (opts[0]?.value ?? null)
                    : (formData.eventId ?? socialLoginEventId ?? null);
            const calendarLink = eventIdForCalendar
                ? getHdeEventCalendarIcsUrl(eventIdForCalendar)
                : undefined;
            const googleCalendarLink = eventIdForCalendar
                ? getHdeEventGoogleCalendarUrl(eventIdForCalendar)
                : undefined;
            const outlookCalendarLink = eventIdForCalendar
                ? getHdeEventOutlookCalendarUrl(eventIdForCalendar)
                : undefined;

            switch (stepId) {
                case 'event-attendance': {
                    const opts = Array.isArray(ctx.locationOptions)
                        ? (ctx.locationOptions as LocationOption[])
                        : [];
                    return {
                        eventId:
                            formData.eventId ??
                            (opts.length > 0 ? (opts[0]?.value ?? null) : null),
                        attendanceReasons: formData.attendanceReasons,
                        otherReason: formData.otherReason,
                        locationOptions: ctx.locationOptions,
                    };
                }
                case 'project-stage':
                    return {
                        projectStage: formData.projectStage,
                        projectType: formData.projectType,
                        projectScope: formData.projectScope,
                        stageConfig: stageCfg,
                    };
                case 'project-scope':
                    return {
                        projectScope: formData.projectScope,
                        projectType: formData.projectType,
                        stageConfig: stageCfg,
                    };
                case 'project-location': {
                    const opts = Array.isArray(ctx.locationOptions)
                        ? (ctx.locationOptions as LocationOption[])
                        : [];
                    return {
                        projectLocation: formData.projectLocation,
                        selectedLocation: formData.selectedLocation,
                        showOtherInput: (
                            formData as { showOtherInput?: boolean }
                        ).showOtherInput,
                        eventCountry: getHdeEventCountry(
                            opts,
                            formData.eventId,
                        ),
                    };
                }
                case 'product-interests':
                    return {
                        productInterests: formData.productInterests,
                        otherProductInterest: formData.otherProductInterest,
                    };
                case 'project-budget':
                    return {
                        budget: formData.budget,
                        projectScope: formData.projectScope,
                        projectType: formData.projectType,
                        stageConfig: stageCfg,
                    };
                case 'guest-invitation': {
                    const me = ctx.user as {
                        FirstName?: string;
                        LastName?: string;
                        Email?: string;
                    } | null;

                    return {
                        bringGuest: formData.bringGuest,
                        guestFormView: formData.guestFormView,
                        guestName: formData.guestName,
                        guestEmail: formData.guestEmail,
                        sameContactDetails: formData.sameContactDetails,
                        primaryName: [me?.FirstName, me?.LastName]
                            .filter(Boolean)
                            .join(' '),
                        primaryEmail: me?.Email ?? '',
                        user: ctx.user,
                    };
                }
                case 'authentication': {
                    const eventId =
                        opts.length > 0
                            ? (opts[0]?.value ?? null)
                            : (formData.eventId ?? socialLoginEventId ?? null);
                    return {
                        hdeFormData: {
                            ...formData,
                            eventId,
                            primaryEventId: getPrimaryEventId(eventId, opts),
                        },
                        showForgotPassword: ctx.showForgotPassword,
                        parentShowEmailForm: ctx.showEmailForm,
                        onBack: ctx.handleAuthEmailFormBack,
                    };
                }
                case 'success':
                    return {
                        onDone: ctx.onClose,
                        registrationType: 'Homeowner' as const,
                        calendarLink,
                        googleCalendarLink,
                        outlookCalendarLink,
                        isSubmitSuccess: ctx.isSubmitSuccess,
                    };
                default:
                    return undefined;
            }
        },
        [socialLoginEventId],
    );

    const buildSubmitPayload = useCallback(
        (formData: FormData, user: User | null) => {
            const me =
                user && (user as { __typename?: string }).__typename === 'Me'
                    ? (user as {
                          FirstName?: string;
                          LastName?: string;
                          Email?: string;
                          Phone?: string;
                      })
                    : null;
            const submitData = new FormData();
            submitData.append('ApplicationType', 'Homeowner');

            const eventIdSource =
                formData.eventId ??
                socialLoginEventId ??
                (locationOptions.length > 0
                    ? (locationOptions[0]?.value ?? null)
                    : null);
            if (eventIdSource) {
                const eventIds = eventIdSource.includes(',')
                    ? eventIdSource
                          .split(',')
                          .map((id) => Number(id.trim()))
                          .filter((id) => !isNaN(id))
                    : [Number(eventIdSource)];
                if (eventIds.length > 0) {
                    submitData.append('EventIDs', JSON.stringify(eventIds));
                }
            }

            if (me) {
                if (me.FirstName) submitData.append('FirstName', me.FirstName);
                if (me.LastName) submitData.append('LastName', me.LastName);
                if (me.Email) submitData.append('Email', me.Email);
                submitData.append('Phone', me.Phone ?? '');
            }
            submitData.append('AutoCreateMember', 'true');
            submitData.append('IsModalSubmission', 'true');
            if (!submitData.has('Phone')) submitData.append('Phone', '');

            submitData.append(
                'BringingGuest',
                formData.bringGuest === true ? 'true' : 'false',
            );
            if (formData.bringGuest === true) {
                if (formData.guestEmail)
                    submitData.append('GuestEmail', formData.guestEmail);
                if (formData.guestName) {
                    const nameParts = formData.guestName
                        .trim()
                        .split(/\s+/)
                        .filter(Boolean);
                    const firstName = nameParts[0] || '';
                    const lastName =
                        nameParts.slice(1).join(' ').trim() || firstName;
                    if (firstName)
                        submitData.append('GuestFirstName', firstName);
                    if (lastName) submitData.append('GuestLastName', lastName);
                }
            }

            if (formData.attendanceReasons.length > 0) {
                submitData.append(
                    'ProjectAttendanceReason',
                    JSON.stringify(
                        formData.attendanceReasons.map((r) => r.value),
                    ),
                );
            }
            if (formData.otherReason) {
                submitData.append(
                    'HDEOtherPleaseSpecify',
                    formData.otherReason,
                );
            }
            if (formData.productInterests.length > 0) {
                const values = formData.productInterests.map((p) =>
                    p.value === 'Other'
                        ? formData.otherProductInterest
                        : p.value,
                );
                submitData.append(
                    'ProjectProductInterest',
                    JSON.stringify(values),
                );
            }
            if (formData.projectType) {
                submitData.append('ProjectType', formData.projectType);
            }
            if (formData.projectScope) {
                submitData.append('ProjectScope', formData.projectScope);
            }
            if (formData.projectStage) {
                submitData.append('ProjectStage', formData.projectStage);
            }
            if (formData.projectLocation) {
                submitData.append('ProjectLocation', formData.projectLocation);
            }
            if (formData.selectedLocation) {
                submitData.append(
                    'HDEBuildHubLocationData',
                    JSON.stringify(formData.selectedLocation),
                );
            }
            if (formData.budget) {
                submitData.append('ProjectBudget', formData.budget);
            }
            if (formData.ownerType) {
                submitData.append(
                    'PropertyOwned',
                    formData.ownerType === 'owner' ? 'true' : 'false',
                );
            }

            return submitData;
        },
        [locationOptions, socialLoginEventId],
    );

    const form = useHDERegistrationForm<FormData>({
        initialFormData: INITIAL_FORM_DATA,
        stepConfigs: HDE_STEP_CONFIGS,
        layout,
        filterVisibleSteps,
        getInitialDataForStep,
        buildSubmitPayload,
        hasGuestInvitation: true,
        locationOptions,
        stageConfig,
        onClose,
        onStepChange,
        onAuthViewChange,
        onAuthLoadingChange,
        socialLoginSubmitted,
        onBackToApplicationType,
        indicatorStepOffset: 1,
        applicationType: 'Homeowner',
        getSelectedEventId: (fd) =>
            resolveHdeEventId(fd.eventId, locationOptions, socialLoginEventId),
        mapExistingSubmissionToFormData: (existing) =>
            mapExistingSubmissionToHomeownerFormData(
                existing,
            ) as Partial<FormData>,
        onSwitchApplicationType,
        suppressExistingSubmissionGate,
        onExistingSubmissionGateResolved,
    });

    return form.renderForm();
}
